import { useEffect, useState } from 'react';
import {
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
  NUMBER_FORMAT_PERCENTAGE,
} from '@ardoq/api-types';
import { Checkbox, NumberInput, TextInput } from '@ardoq/forms';
import {
  formatDateShort,
  parseLocaleDateString,
  formatDateTimeShort,
} from '@ardoq/date-time';
import {
  formatNumber,
  Locale,
  localeCompareInsensitive,
  percentageStoredValueToEditValue,
  percentageEditValueToStoredValue,
} from '@ardoq/locale';
import { Multiselect, Select, SelectOption } from '@ardoq/select';
import { type I18nLocale, t } from '@ardoq/i18n';
import { SurveyErrorMessage } from '../surveyQuestionAtoms';
import { GetUserFeedbackReturn } from '../getUserFeedback';
import { SurveyQuestionOptions } from '../QuestionOptions/SurveyQuestionOptions';
import {
  SurveyTextAreaContainer,
  SurveyTextAreaContainerProps,
} from '../SurveyTextAreaContainer';
import { SurveyReadOnlyOrLockedFieldQuestion } from './SurveyReadOnlyOrLockedFieldQuestion';
import {
  AttributeAnswerValue,
  FetchComponentOptionsWithQuery,
  FieldAnswerValue,
  FieldAnswerValueWithoutDateRange,
  GetAISuggestions,
  PartialAnswer,
} from '../../types';
import { DateOnlyPicker, DateTimePicker } from '@ardoq/date-time-picker';
import { isArray, isNil, isNumber } from 'lodash';
import { isValueStringOrEmpty, isValueEmpty } from '../../utils/stringUtils';
import { Link } from '@ardoq/link';
import { FlexBox } from '@ardoq/layout';

type GetDuplicateNameErrorProps = Pick<
  FieldControlProps,
  | 'attributeName'
  | 'getDuplicate'
  | 'validateDuplicateName'
  | 'value'
  | 'originalValue'
  | 'currentLocale'
  | 'i18nLocale'
>;

export const getDuplicateNameError = ({
  attributeName,
  getDuplicate,
  validateDuplicateName,
  value,
  originalValue,
  currentLocale,
  i18nLocale,
}: GetDuplicateNameErrorProps) => {
  if (
    !attributeName ||
    typeof value !== 'string' ||
    !isValueStringOrEmpty(originalValue)
  )
    return null;
  const duplicate = getDuplicate(value);
  if (
    validateDuplicateName &&
    duplicate &&
    localeCompareInsensitive(value, originalValue ?? '', currentLocale)
  ) {
    // If new value is not equal the original value, and a duplicate exists, show error
    return (
      <FlexBox align="center" gap="xsmall">
        {t(
          i18nLocale,
          'surveysUi:validation.duplicateName',
          'This name is already in use.'
        )}
        <Link
          href={duplicate.path}
          target="_blank"
          rel="noopener noreferrer"
          typography="caption"
        >
          {t(
            i18nLocale,
            'surveysUi:validation.clickToSeeExisting',
            'Click to see the existing entry'
          )}
        </Link>
      </FlexBox>
    );
  }
};

const getNumberValue = (
  value: number | null | undefined
): number | undefined => {
  return !isNil(value) && !isNaN(value) ? value : undefined;
};

type FieldControlProps = {
  userFeedback: GetUserFeedbackReturn;
  surveyId: string;
  componentId: ArdoqId | undefined;
  type: APIFieldType;
  readOnlyOrLocked: boolean;
  originalValue: FieldAnswerValueWithoutDateRange | undefined;
  attributeName?: string;
  parentName: string | undefined;
  questionId: string;
  value: FieldAnswerValueWithoutDateRange | undefined;
  field: APIFieldAttributes | undefined;
  currentLocale: Locale;
  i18nLocale: I18nLocale;
  validateDuplicateName?: boolean;
  componentName: string | null;
  componentTypeName: string;
  isReference?: boolean;
  hasAttemptedSubmission: boolean;
  hasAutocompleteFeature: boolean;
  isEmbedded: boolean;
  hideReadOnlyLabel: boolean;
  questionLabel: string;
  setAnswer: (answer: PartialAnswer) => void;
  getDuplicate: (value: string) => null | { path: string };
  getExistingComponentByName: (name: string) => void;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
  userOptions: SelectOption<ArdoqId>[];
} & Pick<
  SurveyTextAreaContainerProps,
  | 'generateComponentDescription'
  | 'onDescriptionReceived'
  | 'onLLMCouldNotGenerateDescription'
>;

export const SurveyFieldControl = ({
  userFeedback,
  type,
  readOnlyOrLocked,
  originalValue,
  attributeName,
  questionId,
  value,
  field,
  currentLocale,
  i18nLocale,
  surveyId,
  componentId,
  validateDuplicateName,
  hasAttemptedSubmission,
  componentName,
  componentTypeName,
  isReference,
  isEmbedded,
  hasAutocompleteFeature,
  parentName,
  hideReadOnlyLabel,
  questionLabel,
  setAnswer,
  getDuplicate,
  getExistingComponentByName,
  generateComponentDescription,
  onDescriptionReceived,
  onLLMCouldNotGenerateDescription,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  userOptions,
}: FieldControlProps) => {
  const [hasBeenBlurred, setHasBeenBlurred] = useState<boolean>(
    hasAttemptedSubmission
  );

  useEffect(() => {
    setHasBeenBlurred(hasAttemptedSubmission);
  }, [hasAttemptedSubmission]);

  if (readOnlyOrLocked) {
    return (
      <SurveyReadOnlyOrLockedFieldQuestion
        userFeedback={userFeedback}
        type={type}
        field={field}
        currentLocale={currentLocale}
        i18nLocale={i18nLocale}
        attributeName={attributeName}
        value={value}
        parentName={parentName}
        hideReadOnlyLabel={hideReadOnlyLabel}
        userOptions={userOptions}
      />
    );
  }

  const errorMessage = hasBeenBlurred ? userFeedback?.error : undefined;
  const warningMessage = userFeedback?.warning;

  const handleSetAnswer = (value: FieldAnswerValue | AttributeAnswerValue) => {
    setAnswer({
      questionId,
      value,
    });
    if (!isEmbedded) return;
    window.parent.postMessage({ type: 'setNewResponse' }, '*');
  };

  const commonProps = {
    errorMessage,
    warningMessage,
  };

  const onBlur = () => setHasBeenBlurred(true);

  if (type === APIFieldType.TEXT_AREA && isValueStringOrEmpty(value)) {
    return (
      <SurveyTextAreaContainer
        {...commonProps}
        onBlur={onBlur}
        content={value ?? ''}
        onInputChange={handleSetAnswer}
        attributeName={attributeName}
        componentName={componentName}
        componentTypeName={componentTypeName}
        isReference={isReference}
        ariaLabel={questionLabel}
        hasAutocompleteFeature={hasAutocompleteFeature}
        generateComponentDescription={generateComponentDescription}
        onDescriptionReceived={onDescriptionReceived}
        onLLMCouldNotGenerateDescription={onLLMCouldNotGenerateDescription}
        i18nLocale={i18nLocale}
      />
    );
  } else if (
    [APIFieldType.LIST, APIFieldType.SELECT_MULTIPLE_LIST].includes(type) &&
    !isNumber(value) &&
    (isArray(value) || isValueStringOrEmpty(value))
  ) {
    const isMultiselect =
      type === APIFieldType.SELECT_MULTIPLE_LIST && typeof value !== 'string';
    const optionValues = (
      field && typeof field.defaultValue === 'string' ? field.defaultValue : ''
    )
      .split(',')
      .map((optionValue: string) => optionValue.trim())
      .filter(Boolean);

    const mappedValues: SelectOption<string>[] = optionValues.map(
      (optionValue: string) => ({
        label: optionValue,
        value: optionValue,
      })
    );

    const commonSelectProps = {
      escapeClearsValue: false,
      tabSelectsValue: false,
      isClearable: true,
      isSearchable: true,
      closeMenuOnSelect: !isMultiselect,
      options: mappedValues,
      onBlur,
      ...commonProps,
    };

    return isMultiselect ? (
      <Multiselect
        value={
          value
            ? value.filter((selected: string) =>
                mappedValues.find(option => option.value === selected)
              )
            : null
        }
        dataTestId="multiselect"
        aria-label={questionLabel}
        onChange={selected => {
          let newVal;
          if (selected) {
            newVal = selected.map(s => s.value);
          }
          handleSetAnswer(newVal);
        }}
        {...commonSelectProps}
      />
    ) : (
      <Select
        value={mappedValues.find(option => option.value === value)}
        onChange={selected => {
          handleSetAnswer(selected?.value ?? null);
        }}
        dataTestId="select"
        aria-label={questionLabel}
        {...commonSelectProps}
      />
    );
  } else if (type === APIFieldType.USER && isValueStringOrEmpty(value)) {
    return (
      <Select
        value={userOptions.find(option => option.value === value)}
        onChange={selected => handleSetAnswer(selected?.value ?? null)}
        options={userOptions}
        escapeClearsValue={false}
        tabSelectsValue={false}
        isClearable
        isSearchable
        onBlur={onBlur}
        dataTestId="user-select"
        aria-label={questionLabel}
        {...commonProps}
      />
    );
  } else if (type === APIFieldType.DATE_ONLY && isValueStringOrEmpty(value)) {
    return (
      <DateOnlyPicker
        date={parseLocaleDateString(value ?? '', currentLocale)}
        config={{
          formatter: formatDateShort,
          locale: currentLocale,
        }}
        onChange={newVal => handleSetAnswer(newVal)}
        onCloseDatepicker={onBlur}
        aria-label={questionLabel}
        {...commonProps}
      />
    );
  } else if (type === APIFieldType.DATE_TIME && isValueStringOrEmpty(value)) {
    return (
      <DateTimePicker
        date={parseLocaleDateString(value ?? '', currentLocale)}
        config={{
          useTimePicker: true,
          formatter: formatDateTimeShort,
          locale: currentLocale,
        }}
        onChange={newVal => handleSetAnswer(newVal && newVal.toISOString())}
        onCloseDatepicker={onBlur}
        aria-label={questionLabel}
        {...commonProps}
      />
    );
  } else if (
    type === APIFieldType.CHECKBOX &&
    (typeof value === 'boolean' || isValueEmpty(value))
  ) {
    return (
      <Checkbox
        data-test-id="checkbox"
        isChecked={value === undefined ? null : value}
        onChange={() => handleSetAnswer(!value)}
        aria-label={questionLabel}
        {...commonProps}
      />
    );
  } else if (
    type === APIFieldType.NUMBER &&
    (isNumber(value) || isValueEmpty(value))
  ) {
    const isPercentage =
      field?.numberFormatOptions === NUMBER_FORMAT_PERCENTAGE;
    const numberValue = getNumberValue(value);

    // Convert stored value (0.25) to edit value (25) for percentages
    const editValue =
      isPercentage && numberValue !== undefined
        ? percentageStoredValueToEditValue(numberValue)
        : numberValue;

    // Display value when not focused (formatted: "25%")
    const displayValue = formatNumber(
      value,
      field?.numberFormatOptions,
      currentLocale
    );

    // Handle value changes: convert back from percentage (25 → 0.25)
    const handleValueChange = (number: number) => {
      const storedValue = isPercentage
        ? percentageEditValueToStoredValue(number)
        : number;
      handleSetAnswer(getNumberValue(storedValue));
    };

    return (
      <NumberInput
        displayValue={displayValue}
        value={editValue}
        onValueChange={handleValueChange}
        data-test-id="number-input"
        aria-label={questionLabel}
        onBlur={onBlur}
        allowDecimals
        {...commonProps}
      />
    );
  } else if (attributeName === 'parent' && isValueStringOrEmpty(value)) {
    return (
      <SurveyQuestionOptions
        userFeedback={userFeedback}
        onChange={(
          option: SelectOption<string> | SelectOption<string>[] | null
        ) => {
          if (Array.isArray(option)) return;
          handleSetAnswer(option ? option.value : null);
        }}
        surveyId={surveyId}
        questionId={questionId}
        componentId={componentId}
        value={value}
        currentLocale={currentLocale}
        i18nLocale={i18nLocale}
        identifierFieldName={undefined}
        startingValue={parentName}
        questionLabel={questionLabel}
        getAISuggestions={getAISuggestions}
        fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
      />
    );
  }

  const duplicateNameError = getDuplicateNameError({
    attributeName,
    getDuplicate,
    validateDuplicateName,
    value,
    originalValue,
    currentLocale,
    i18nLocale,
  });
  const stringValue = typeof value === 'string' ? value : '';
  return (
    <>
      <TextInput
        value={stringValue}
        type={type.toLowerCase()}
        onChange={event => {
          if (attributeName === 'name' && getExistingComponentByName) {
            getExistingComponentByName(event.target.value);
          }
          handleSetAnswer(event.target.value);
        }}
        dataTestId="text-input"
        aria-label={questionLabel}
        onBlur={onBlur}
        {...commonProps}
      />
      {duplicateNameError && (
        <SurveyErrorMessage>{duplicateNameError}</SurveyErrorMessage>
      )}
    </>
  );
};
